// frontend/modules/service-metrics/stores/service-metrics-store.ts
import { create } from 'zustand';
import type { 
    ServiceMetricDTO, 
    ServiceMetricSummaryDTO, 
    ServiceHealthDTO,
    ServiceMetricLogDTO,
} from '@/shared/api/model';
import type { ServiceMetricsFilters } from '../types/service-metrics.types';

interface ServiceMetricsState {
    // Data
    serviceNames: string[];
    metrics: ServiceMetricDTO[];
    selectedServiceSummary: ServiceMetricSummaryDTO | null;
    serviceHealth: ServiceHealthDTO | null;
    serviceLogs: ServiceMetricLogDTO[];
    topServices: ServiceMetricDTO[];
    servicesWithFailures: ServiceMetricDTO[];
    
    // UI State
    isLoading: boolean;
    error: string | null;
    selectedService: string | null;
    filters: ServiceMetricsFilters;
    
    // Pagination
    currentPage: number;
    pageSize: number;
    totalElements: number;
    
    // Actions
    setServiceNames: (names: string[]) => void;
    setMetrics: (metrics: ServiceMetricDTO[]) => void;
    setSelectedServiceSummary: (summary: ServiceMetricSummaryDTO | null) => void;
    setServiceHealth: (health: ServiceHealthDTO | null) => void;
    setServiceLogs: (logs: ServiceMetricLogDTO[]) => void;
    setTopServices: (services: ServiceMetricDTO[]) => void;
    setServicesWithFailures: (services: ServiceMetricDTO[]) => void;
    setIsLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    setSelectedService: (service: string | null) => void;
    setFilters: (filters: ServiceMetricsFilters) => void;
    setCurrentPage: (page: number) => void;
    setPageSize: (size: number) => void;
    setTotalElements: (total: number) => void;
    resetState: () => void;
}

const initialState = {
    serviceNames: [],
    metrics: [],
    selectedServiceSummary: null,
    serviceHealth: null,
    serviceLogs: [],
    topServices: [],
    servicesWithFailures: [],
    isLoading: false,
    error: null,
    selectedService: null,
    filters: { days: 7 },
    currentPage: 0,
    pageSize: 20,
    totalElements: 0,
};

export const useServiceMetricsStore = create<ServiceMetricsState>((set) => ({
    ...initialState,
    
    setServiceNames: (serviceNames) => set({ serviceNames }),
    setMetrics: (metrics) => set({ metrics }),
    setSelectedServiceSummary: (selectedServiceSummary) => set({ selectedServiceSummary }),
    setServiceHealth: (serviceHealth) => set({ serviceHealth }),
    setServiceLogs: (serviceLogs) => set({ serviceLogs }),
    setTopServices: (topServices) => set({ topServices }),
    setServicesWithFailures: (servicesWithFailures) => set({ servicesWithFailures }),
    setIsLoading: (isLoading) => set({ isLoading }),
    setError: (error) => set({ error }),
    setSelectedService: (selectedService) => set({ selectedService }),
    setFilters: (filters) => set((state) => ({ filters: { ...state.filters, ...filters } })),
    setCurrentPage: (currentPage) => set({ currentPage }),
    setPageSize: (pageSize) => set({ pageSize }),
    setTotalElements: (totalElements) => set({ totalElements }),
    resetState: () => set(initialState),
}));
